import type { ComponentType, ReactNode } from "react";
import { useRef, useState } from "react";

import { useTranslation } from "react-i18next";

import { Tooltip } from "@similarweb/ui-components";
import { Flex } from "@similarweb/ui-components/foundations";

import type { MessageCollapseStatus } from "app/pages/sales-intelligence/modules/ai-agent/components/ChatMessage/types";
import { CopyButton } from "app/pages/sales-intelligence/modules/ai-agent/components/CopyButton/CopyButton";
import { DownloadButton } from "app/pages/sales-intelligence/modules/ai-agent/components/DownloadButton/DownloadButton";
import FeedbackButton from "app/pages/sales-intelligence/modules/ai-agent/components/FeedbackButton/FeedbackButton";
import { MarkdownMessage } from "app/pages/sales-intelligence/modules/ai-agent/components/MarkdownMessage/MarkdownMessage";
import type { MarkdownMessageProps } from "app/pages/sales-intelligence/modules/ai-agent/components/MarkdownMessage/types";
import AIAgentTrackingService from "app/pages/sales-intelligence/modules/ai-agent/services/tracking/trackingService";

import { StyledIconButton, StyledMessage } from "./styles";

type AIResponseMessageProps = {
    content?: string;
    isLoading?: boolean;
    reaction?: string;
    messageId?: string;
    messageRef?: React.RefObject<HTMLDivElement | null>;
    mdComponentProps?: Record<string, unknown>;
    markdownComponent?: ComponentType<MarkdownMessageProps>;
    loader?: ReactNode;
    withControls?: boolean;
    onFeedback?: (reaction: string) => void;
    onCopy?: () => void;
};

const AIResponseMessage = ({
    content,
    isLoading,
    reaction,
    messageId,
    messageRef,
    mdComponentProps,
    markdownComponent: MarkdownComponent = MarkdownMessage,
    loader,
    withControls = true,
    onFeedback,
    onCopy,
}: AIResponseMessageProps) => {
    const { t } = useTranslation();
    const markdownRef = useRef<HTMLDivElement | null>(null);
    const controlsRef = useRef<HTMLDivElement | null>(null);
    const [collapseStatus, setCollapseStatus] = useState<MessageCollapseStatus>("expanded");
    const isBrief = isLoading ? false : /#{1,3}\s/.test(content || "");

    const handleCollapseToggle = (status: MessageCollapseStatus) => {
        setCollapseStatus(status);
        const action = status === "collapsed" ? "Collapse all" : "Expand all";
        AIAgentTrackingService.meeting.trackBriefMessageToggleAll(action);
        setTimeout(() => {
            controlsRef.current?.scrollIntoView({ behavior: "smooth", block: "end" });
        }, 300);
    };

    return (
        <>
            <StyledMessage
                variant="ai"
                data-automation="agent-message"
                ref={(value) => {
                    markdownRef.current = value;
                    if (messageRef) {
                        (messageRef as React.MutableRefObject<HTMLDivElement | null>).current = value;
                    }
                }}
            >
                <MarkdownComponent
                    content={content ?? ""}
                    messageId={messageId}
                    componentProps={mdComponentProps}
                    loader={loader}
                    collapseStatus={collapseStatus}
                    setCollapseStatus={setCollapseStatus}
                    isLoading={isLoading}
                />
            </StyledMessage>
            {isLoading || !withControls ? null : (
                <Flex
                    alignItems="center"
                    margin="16px 0 0"
                    width="100%"
                    ref={controlsRef}
                >
                    <CopyButton
                        markdownRef={markdownRef}
                        onCopy={onCopy}
                    />
                    <FeedbackButton
                        key="like"
                        reaction="like"
                        isActive={reaction === "like"}
                        onFeedback={() => onFeedback?.("like")}
                        dataAutomation="like-button"
                    />

                    <FeedbackButton
                        key="dislike"
                        reaction="dislike"
                        isActive={reaction === "dislike"}
                        onFeedback={() => onFeedback?.("dislike")}
                        dataAutomation="dislike-button"
                    />
                    {isBrief ? (
                        <>
                            <Tooltip content={t("si.pages.ai_agent.collapse")}>
                                <StyledIconButton
                                    iconName="collapsed"
                                    iconSize="xs"
                                    type="flat"
                                    onClick={() => handleCollapseToggle("collapsed")}
                                    isDisabled={collapseStatus === "collapsed"}
                                    dataAutomation="collapse-button"
                                />
                            </Tooltip>
                            <Tooltip content={t("si.pages.ai_agent.expand")}>
                                <StyledIconButton
                                    iconName="expand"
                                    iconSize="xs"
                                    type="flat"
                                    onClick={() => handleCollapseToggle("expanded")}
                                    isDisabled={collapseStatus === "expanded"}
                                    dataAutomation="expand-button"
                                />
                            </Tooltip>
                            {messageId ? (
                                <Flex margin="0 0 0 auto">
                                    <DownloadButton messageId={messageId} />
                                </Flex>
                            ) : null}
                        </>
                    ) : null}
                </Flex>
            )}
        </>
    );
};

export default AIResponseMessage;
